import type { ProviderKind, ServerProvider } from "@cmmd-forge/contracts";
import { isServerProviderExecutable } from "@cmmd-forge/shared/providerExecution";

import { resolveExecutionOfferability } from "../providerPickerAvailability";
import {
  resolveProviderEligibility,
  resolveProvisioningProviderEligibility,
  type ForgeProviderAccessState,
  type ProviderCredentialMode,
  type ProviderIneligibilityReason,
} from "./forgeProviderAccess";
import {
  isUnbuiltPickerProvider,
  NOT_ON_THIS_ENVIRONMENT,
  providerKindForRail,
  UNBUILT,
  USABLE,
  type PickerRailProvider,
  type ShellProviderAvailability,
} from "./providerAvailabilityVocabulary";

/**
 * The Shell's one answer to "may this person pick this provider here, and if
 * not, why".
 *
 * WHY THIS EXISTS
 *
 * Three systems each hold part of the answer and none of them holds all of it.
 * CMMD owns the Provider Offer and the person's authority; the Environment owns
 * the Provider Inventory and publishes it with an explicit availability since
 * FOR-36364; Forge owns which providers the product has shipped. The Shell used
 * to be handed a filtered list and had to infer meaning from what was missing,
 * so four different states rendered as one "Coming soon." string. See
 * `docs/adr/withheld-providers-carry-their-reason.md` and the glossary in
 * `CONTEXT.md` section "Provider availability".
 *
 * This joins the three and never reads a missing entry as a state. It adds only
 * what the Environment cannot know (Not Offered, and a credential still being
 * delivered) and otherwise repeats what the wire said.
 *
 * The rail vocabulary (`PickerRailProvider`, the roadmap gate, the withheld
 * detail types, `ShellProviderAvailability` itself) lives in
 * `providerAvailabilityVocabulary.ts`. Only the members other modules actually
 * import from here are re-exported, so every existing caller keeps working
 * unchanged; `USABLE`, `UNBUILT`, `NOT_ON_THIS_ENVIRONMENT` and
 * `ShellOnlyProviderUnavailableDetail` stay internal to the two files that use
 * them.
 */
export {
  isUnbuiltPickerProvider,
  providerKindForRail,
  UNBUILT_PICKER_PROVIDERS,
  type NotUsableShellDetail,
  type PickerRailProvider,
  type ShellProviderAvailability,
  type ShellProviderUnavailableDetail,
} from "./providerAvailabilityVocabulary";

/**
 * What one Environment said about one provider, in the Shell's vocabulary.
 *
 * THE ONLY DECODER. Both readers call this: the join, which then adds what
 * CMMD knows, and the picker's own fallback in `modelPickerRail.ts`, which has
 * nothing else to add. They used to decode a snapshot separately and disagreed
 * about a snapshot from an Environment older than FOR-36364 whose probe had
 * failed: the join said Not on this Environment, the rail said usable, and the
 * row printed "Unavailable" with no reason a person could act on.
 */
export function resolvePublishedProviderAvailability(
  snapshot: ServerProvider | undefined,
  hasLiveInventory: boolean,
): ShellProviderAvailability {
  // Absent. An inventory that exists and does not list a provider is the
  // Environment saying it cannot run it; no inventory at all is a question
  // nobody has asked yet, and greying out a working provider because a sprite
  // is asleep is the failure FOR-36342 fixed.
  if (snapshot === undefined) return hasLiveInventory ? NOT_ON_THIS_ENVIRONMENT : USABLE;
  if (snapshot.availability === "unavailable") {
    const reason = snapshot.unavailableReason ?? "not-on-this-environment";
    // A probe that failed, timed out, or errored is not evidence the CLI is
    // missing (forge#3588): say so distinctly rather than repeating "is not
    // installed" for a reading nobody actually confirmed.
    const detail =
      reason === "not-on-this-environment" && snapshot.availabilityCheck === "could-not-check"
        ? "could-not-check"
        : snapshot.unavailableDetail;
    return detail === undefined
      ? { state: "withheld", reason }
      : { state: "withheld", reason, detail };
  }
  if (snapshot.availability === "available") return USABLE;
  // No availability field at all, so this publisher predates FOR-36364 and
  // sent no reason. Its own executability gate is the one thing left to read,
  // and it is the same gate the current publisher restates.
  if (isServerProviderExecutable(snapshot)) return USABLE;
  // Except for one shape it still describes well enough. The CLI is there and
  // nobody has signed in: the rail marks that with a visible sign-in dot and
  // leaves the row selectable on purpose (forge#2002, informing rather than
  // hard-blocking), and calling it "not installed" would both lie and take the
  // dot away. A current Environment sends not-usable / no-connection here, so
  // this only ever fires for a legacy publisher.
  return awaitsPersonalSignIn(snapshot) ? USABLE : NOT_ON_THIS_ENVIRONMENT;
}

/** A snapshot whose CLI is present and whose only gap is a personal sign-in. */
function awaitsPersonalSignIn(snapshot: ServerProvider): boolean {
  return (
    snapshot.installed &&
    snapshot.auth.status === "unauthenticated" &&
    snapshot.auth.provenance?.credentialScope !== "proxied"
  );
}

/**
 * What CMMD says about this person and this provider, ignoring the Environment.
 *
 * `resolveProvisioningProviderEligibility` is the person's authority and is not
 * reimplemented here: it answers offer, personal connection and reconnect
 * without consulting a snapshot, which is exactly what is needed before the
 * Environment gets a say.
 */
function authorityAvailability(
  providerKind: ProviderKind,
  access: ForgeProviderAccessState,
): ShellProviderAvailability | null {
  const authority = resolveProvisioningProviderEligibility({ providerKind, access });
  return authority.eligible ? null : fromIneligibilityReason(authority.reason);
}

function fromIneligibilityReason(
  reason: ProviderIneligibilityReason,
): ShellProviderAvailability | null {
  switch (reason) {
    case "access-loading":
      return { state: "unresolved", cause: "loading" };
    case "access-error":
      return { state: "unresolved", cause: "error" };
    case "not-offered":
      return { state: "withheld", reason: "not-offered" };
    case "personal-credential-required":
      return { state: "withheld", reason: "not-usable", detail: "no-connection" };
    case "personal-credential-reconnect-required":
      return { state: "withheld", reason: "not-usable", detail: "reconnect-required" };
    case "native-runtime-not-ready":
      return { state: "withheld", reason: "not-usable", detail: "delivery-pending" };
    case "environment-not-running":
      return { state: "withheld", reason: "not-usable", detail: "environment-not-running" };
    // The one reason that is about the Environment rather than the person. It
    // is also the one this join must never take at face value on the pick
    // projection, because the rule that produces it reads an absent or
    // non-executable snapshot: the absence-to-state mapping this program
    // exists to delete. `environmentAvailability` decides by projection.
    case "runtime-unavailable":
      return null;
  }
}

/**
 * Which verdict one evaluation is answering. The whole difference between the
 * two is how the transient readiness answer in `environmentAvailability` is
 * treated; see
 * `docs/adr/pick-and-run-are-projections-of-one-availability-evaluation.md`.
 */
export type AvailabilityProjection = "pick" | "run";

/** The two answers one evaluation gives: what a picker renders, what a send obeys. */
export interface ProviderUseVerdict {
  readonly pick: ShellProviderAvailability;
  readonly run: ShellProviderAvailability;
}

/** Everything the join needs to judge one provider, Environment inventory included. */
export interface LockedAvailabilityInput {
  readonly providerKind: ProviderKind;
  readonly snapshot: ServerProvider | undefined;
  readonly access: ForgeProviderAccessState;
  readonly credentialMode: ProviderCredentialMode;
  readonly hasLiveInventory: boolean;
}

/**
 * The one evaluator behind both verdicts for a picker row: one input bundle,
 * one walk of the join, two answers. Every public entry below is a projection
 * of this one; the divergence class forge#3385 shipped to production was a
 * send path that recomputed its own answer instead.
 */
export function resolveProviderUseVerdict(input: {
  readonly provider: PickerRailProvider;
  readonly snapshot: ServerProvider | undefined;
  readonly access: ForgeProviderAccessState;
  readonly credentialMode: ProviderCredentialMode;
  readonly hasLiveInventory: boolean;
}): ProviderUseVerdict {
  // The roadmap gate is a rail decision, so it withholds both projections at
  // once: a provider the product has not built has no run to gate either.
  if (isUnbuiltPickerProvider(input.provider)) return { pick: UNBUILT, run: UNBUILT };
  const providerKind = providerKindForRail(input.provider);
  if (providerKind === null) return { pick: UNBUILT, run: UNBUILT };
  return resolveLockedProviderUseVerdict({ ...input, providerKind });
}

/** `resolveProviderUseVerdict` for a `ProviderKind`, skipping the roadmap gate. */
export function resolveLockedProviderUseVerdict(
  input: LockedAvailabilityInput,
): ProviderUseVerdict {
  return {
    pick: lockedAvailability(input, "pick"),
    run: lockedAvailability(input, "run"),
  };
}

export function resolveShellProviderAvailability(input: {
  readonly provider: PickerRailProvider;
  readonly snapshot: ServerProvider | undefined;
  readonly access: ForgeProviderAccessState;
  readonly credentialMode: ProviderCredentialMode;
  /** Whether an Environment has published an inventory at all. */
  readonly hasLiveInventory: boolean;
}): ShellProviderAvailability {
  return resolveProviderUseVerdict(input).pick;
}

/**
 * The join's answer for a `ProviderKind` a thread is already locked to,
 * skipping the picker's roadmap gate (`UNBUILT_PICKER_PROVIDERS`).
 *
 * That gate decides whether the picker may offer a provider for a NEW
 * selection; it is a product decision about the rail, not a fact about
 * whether an Environment can run the provider. A thread already locked to
 * cursor or opencode over a local/runtime credential got there before the
 * picker had a say (they have a real detection Layer and adapter, see
 * `UNBUILT_PICKER_PROVIDERS`), and un-selecting the row the picker withheld
 * must not turn into "Coming soon." for a session already running on it.
 * `resolveShellProviderAvailability` is the picker's entry point; this is the
 * Composer's, and both end here once a roadmap row is not in question.
 */
export function resolveLockedProviderAvailability(
  input: LockedAvailabilityInput,
): ShellProviderAvailability {
  return lockedAvailability(input, "pick");
}

/**
 * The providers a send may build on: those whose Run verdict is usable.
 *
 * Replaces the send path's old filter over the raw eligibility subset, a
 * second predicate that read live readiness without the published wire and
 * could disagree with the picker; forge#3385 sent a person's words to a
 * provider they did not pick through exactly that gap. Eligibility stays the
 * join's backend; product code judges providers through verdicts now.
 *
 * `hasLiveInventory` still cannot change whether an entry's own snapshot is
 * absent (every entry here carries one, by construction), but it does change
 * whether a Not on this Environment reading counts as confirmed: a catalog
 * sourced from CMMD's last-known inventory (forge#3588) never carries a
 * confirmed answer, so the caller passes its own inventory source through
 * rather than a hardcoded live one.
 */
export function resolveRunnableProviderSnapshots(input: {
  readonly providers: ReadonlyArray<ServerProvider>;
  readonly access: ForgeProviderAccessState;
  readonly credentialMode: ProviderCredentialMode;
  readonly hasLiveInventory: boolean;
}): ReadonlyArray<ServerProvider> {
  return input.providers.filter(
    (provider) =>
      lockedAvailability(
        {
          providerKind: provider.provider,
          snapshot: provider,
          access: input.access,
          credentialMode: input.credentialMode,
          hasLiveInventory: input.hasLiveInventory,
        },
        "run",
      ).state === "usable",
  );
}

function lockedAvailability(
  input: LockedAvailabilityInput,
  projection: AvailabilityProjection,
): ShellProviderAvailability {
  const { providerKind } = input;
  if (input.snapshot?.unavailableReason === "unbuilt") return UNBUILT;

  // Local and desktop runtimes have no CMMD authority to consult: the
  // Environment's own inventory and auth are the whole answer.
  if (input.credentialMode === "runtime") {
    return environmentAvailability({ ...input, providerKind }, projection);
  }
  if (input.access.status !== "ready") {
    return { state: "unresolved", cause: input.access.status };
  }
  const offerability = resolveExecutionOfferability(providerKind, input.access.policy);
  // Both `org-disabled` and `policy-unavailable` land here. The person cannot
  // act on the difference and the Offer is CMMD's to explain; what matters is
  // that Forge is not offering the provider and says so.
  if (!offerability.offered) return { state: "withheld", reason: "not-offered" };

  const authority = authorityAvailability(providerKind, input.access);
  if (authority !== null) return authority;

  return environmentAvailability({ ...input, providerKind }, projection);
}

/**
 * The Environment's half, once CMMD has said the person may use the provider.
 *
 * Order matters: a credential still being delivered is Not Usable and must be
 * read before "not installed", because the CLI being absent is often the same
 * moment the credential has not landed, and telling a person to report a broken
 * Environment when they only need to wait is the worse of the two mistakes.
 */
function environmentAvailability(
  input: LockedAvailabilityInput,
  projection: AvailabilityProjection,
): ShellProviderAvailability {
  const live = resolveProviderEligibility({
    provider: input.snapshot,
    providerKind: input.providerKind,
    access: input.access,
    credentialMode: input.credentialMode,
  });
  // The two things CMMD knows that the Environment cannot: that the Environment
  // itself cannot take a delivery, and that a credential is on its way. The
  // first is read first, because the readiness answer for a dead Environment
  // keeps the row's real delivery status and that status may still say
  // pending. Everything else the snapshot answers for itself.
  if (!live.eligible && live.reason === "environment-not-running") {
    return { state: "withheld", reason: "not-usable", detail: "environment-not-running" };
  }
  if (!live.eligible && live.reason === "native-runtime-not-ready") {
    return { state: "withheld", reason: "not-usable", detail: "delivery-pending" };
  }
  // The one place the two projections part. `runtime-unavailable` is the
  // eligibility rule reading an absent or non-executable snapshot: the
  // absence-to-state mapping this join exists to delete. Pick discards it and
  // lets the published wire answer, which is why a sleeping sprite greys
  // nothing out (FOR-36342). A run keeps the old default -- the same
  // Not-on-this-Environment verdict regardless of what the wire's own
  // `availability` field claims, which is what still refuses the contradictory
  // "available" snapshot below in `resolvePublishedProviderAvailability`'s
  // legacy fallback -- UNLESS the published verdict is SPECIFICALLY a
  // Not on this Environment reading this join cannot trust as confirmed
  // (forge#3588): a live probe that failed, timed out, or errored, or a
  // last-known catalog entry with no live connection to confirm it at all.
  // Either way the provider may really be there, and a send that starts it
  // surfaces a real error if it is not, so a run proceeds only in that one
  // named case.
  const published = resolvePublishedProviderAvailability(input.snapshot, input.hasLiveInventory);
  if (!live.eligible && live.reason === "runtime-unavailable" && projection === "run") {
    return unconfirmedNotOnThisEnvironment(published, input.hasLiveInventory)
      ? USABLE
      : NOT_ON_THIS_ENVIRONMENT;
  }
  return published;
}

/**
 * Whether the published verdict is specifically a Not on this Environment
 * reading this join cannot trust as confirmed: a live probe that could not
 * check, or any reading sourced from a catalog with no live connection to
 * confirm it. Every other withheld reason (Not Usable, Not Offered) and every
 * confirmed miss keep the run projection's old default refusal (forge#3588).
 */
function unconfirmedNotOnThisEnvironment(
  published: ShellProviderAvailability,
  hasLiveInventory: boolean,
): boolean {
  return (
    published.state === "withheld" &&
    published.reason === "not-on-this-environment" &&
    (published.detail === "could-not-check" || !hasLiveInventory)
  );
}
